/**
 * BoardToolbar — Inline toolbar controls for the Board panel.
 * Renders inside the whiteboard-panel__topbar (no own bar).
 */

import React, { useCallback, useRef, useState, useEffect } from 'react'
import { useBoard } from './useBoard'
import { EmbedPicker, type EmbedKind, type PickedEmbed } from '../whiteboard/EmbedPicker'
import { useAuthStore } from '../../store/auth'

const btnStyle: React.CSSProperties = {
  background: '#eee',
  border: '1px solid #ddd',
  color: '#333',
  borderRadius: 4,
  padding: '3px 10px',
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'inherit',
}

interface InsertItem {
  label: string
  kind: EmbedKind | 'checklist'
}

const INSERT_ITEMS: InsertItem[] = [
  { label: 'Szene', kind: 'scene' },
  { label: 'Audit', kind: 'audit' },
  { label: 'Dok', kind: 'doc' },
  { label: 'Report', kind: 'report' },
  { label: 'Notiz', kind: 'note' },
  { label: 'Checkliste', kind: 'checklist' },
]

export function BoardToolbar() {
  const viewport = useBoard((s) => s.viewport)
  const setViewport = useBoard((s) => s.setViewport)
  const addShape = useBoard((s) => s.addShape)
  const projectId = useAuthStore((s) => s.currentProjectId) || ''
  const project = useAuthStore((s) => s.currentProject)
  const projectCloudIds = (project?.pointClouds || []).map((c: any) => c.id)

  const [menuOpen, setMenuOpen] = useState(false)
  const [pickerKind, setPickerKind] = useState<EmbedKind | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('pointerdown', handler)
    return () => document.removeEventListener('pointerdown', handler)
  }, [menuOpen])

  const viewportCenterWorld = useCallback(() => {
    const cx = (window.innerWidth / 2 - viewport.x) / viewport.zoom
    const cy = (window.innerHeight / 2 - viewport.y) / viewport.zoom
    return { x: cx - 140, y: cy - 100 }
  }, [viewport])

  const onInsertClick = useCallback(
    (item: InsertItem) => {
      setMenuOpen(false)
      if (item.kind === 'checklist') {
        const pos = viewportCenterWorld()
        addShape({ type: 'checklist', x: pos.x, y: pos.y, w: 280, h: 240, props: { title: 'Neue Checkliste', items: [] } })
      } else {
        setPickerKind(item.kind)
      }
    },
    [addShape, viewportCenterWorld],
  )

  const handlePick = useCallback(
    (picked: PickedEmbed) => {
      setPickerKind(null)
      const pos = viewportCenterWorld()
      const sz = { w: 280, h: 200 }
      switch (picked.kind) {
        case 'scene': addShape({ type: 'scene', ...pos, ...sz, props: { sceneId: picked.id, title: picked.title } }); break
        case 'audit': addShape({ type: 'audit', ...pos, ...sz, props: { auditId: picked.id, title: picked.title } }); break
        case 'doc': addShape({ type: 'doc', ...pos, ...sz, props: { docId: picked.id, title: picked.title } }); break
        case 'report': addShape({ type: 'report', ...pos, ...sz, props: { reportId: picked.id, title: picked.title, excerpt: (picked as any).excerpt || '' } }); break
        case 'note': addShape({ type: 'note', ...pos, ...sz, props: { noteId: picked.id, title: picked.title } }); break
      }
    },
    [addShape, viewportCenterWorld],
  )

  const zoomIn = useCallback(() => setViewport({ zoom: Math.min(5, viewport.zoom * 1.2) }), [viewport.zoom, setViewport])
  const zoomOut = useCallback(() => setViewport({ zoom: Math.max(0.1, viewport.zoom / 1.2) }), [viewport.zoom, setViewport])
  const zoomFit = useCallback(() => setViewport({ x: 0, y: 0, zoom: 1 }), [setViewport])

  return (
    <>
      {/* Insert dropdown */}
      <div ref={menuRef} style={{ position: 'relative', marginLeft: 8 }}>
        <button type="button" style={btnStyle} onClick={() => setMenuOpen((o) => !o)}>
          + Einfügen
        </button>
        {menuOpen && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4,
            background: '#fff', border: '1px solid #ddd', borderRadius: 6,
            overflow: 'hidden', zIndex: 100, minWidth: 140,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            {INSERT_ITEMS.map((item) => (
              <button
                key={item.kind} type="button"
                onClick={() => onInsertClick(item)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  background: 'none', border: 'none', color: '#333',
                  padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Embed picker modal */}
      {pickerKind && (
        <EmbedPicker
          kind={pickerKind}
          projectId={projectId}
          projectCloudIds={projectCloudIds}
          onSelect={handlePick}
          onClose={() => setPickerKind(null)}
        />
      )}
    </>
  )
}
